import React, { useState, useEffect, useRef } from "react";
import { fetchCars, uploadImage, createSocialPost, updateSocialPost } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { X, Upload, Loader2, Facebook, Instagram, Car as CarIcon, Send, CalendarClock, FileText } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const imgSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const buildCaption = (car) => {
  const title = car.title || "";
  const year = car.year ? String(car.year) : "";
  const heading = year && !title.startsWith(year) ? `${year} ${title}`.trim() : title;
  const brand = title.replace(/^\d{4}\s*/, "").split(" ")[0];
  const tag = brand.replace(/[^a-zA-Z0-9]/g, "");
  return [
    heading,
    "",
    car.short || car.description?.slice(0, 180) || "A stunning example, now available at GPM Classics.",
    "",
    `${car.priceLabel || "POA"} · Based in Devon, UK`,
    "",
    "Enquire today — we'd love to tell you more.",
    "",
    `#classiccars #classiccarsforsale ${tag ? "#" + tag : ""} #GPMClassics #carsofinstagram`.trim(),
  ].join("\n");
};

const PostComposer = ({ post, onClose, onSaved }) => {
  const editing = !!post;
  const [cars, setCars] = useState([]);
  const [channels, setChannels] = useState(post?.channels || ["facebook", "instagram"]);
  const [caption, setCaption] = useState(post?.caption || "");
  const [images, setImages] = useState(post?.images || []);
  const [carId, setCarId] = useState(post?.carId || "");
  const [carTitle, setCarTitle] = useState(post?.carTitle || "");
  const [scheduledAt, setScheduledAt] = useState(post?.scheduledAt || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => { fetchCars().then(setCars).catch(() => setCars([])); }, []);

  const toggleChannel = (ch) => setChannels((p) => (p.includes(ch) ? p.filter((c) => c !== ch) : [...p, ch]));

  const pickCar = (id) => {
    setCarId(id);
    const car = cars.find((c) => c.id === id);
    if (!car) { setCarTitle(""); return; }
    setCarTitle(car.title);
    setCaption(buildCaption(car));
    setImages((car.images || []).slice(0, 10));
    toast({ title: "Post built from car", description: `${car.title} — caption & photos added.` });
  };

  const doUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of list) urls.push((await uploadImage(f)).url);
      setImages((p) => [...p, ...urls]);
    } catch (e) { toast({ title: "Upload failed", variant: "destructive" }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (i) => setImages((p) => p.filter((_, idx) => idx !== i));

  const save = async (status) => {
    if (!channels.length) return toast({ title: "Pick at least one channel", variant: "destructive" });
    if (!caption.trim()) return toast({ title: "Add a caption", variant: "destructive" });
    if (status === "scheduled" && !scheduledAt) return toast({ title: "Choose a date & time to schedule", variant: "destructive" });
    setBusy(true);
    const payload = { channels, caption, images, carId, carTitle, status, scheduledAt: status === "scheduled" ? scheduledAt : null };
    try {
      if (editing) await updateSocialPost(post.id, payload);
      else await createSocialPost(payload);
      toast({ title: status === "published" ? "Post published (demo)" : status === "scheduled" ? "Post scheduled" : "Draft saved" });
      onSaved();
    } catch (e) { toast({ title: "Could not save post", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="post-composer">
      <div className="bg-white w-full max-w-4xl my-8 border border-black/10">
        <div className="flex items-center justify-between px-6 py-4 gpm-bg-green">
          <h3 className="font-serif-display text-lg font-bold gpm-text-cream">{editing ? "Edit post" : "Create post"}</h3>
          <button onClick={onClose} data-testid="composer-close" className="gpm-text-cream hover:gpm-text-gold"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 p-6">
          {/* Left: form */}
          <div className="space-y-5">
            <div>
              <label className={labelCls}><CarIcon size={12} className="inline mr-1" /> Build from a car (optional)</label>
              <select data-testid="composer-car-select" className={inputCls} value={carId} onChange={(e) => pickCar(e.target.value)}>
                <option value="">— Start blank —</option>
                {cars.map((c) => <option key={c.id} value={c.id}>{`${c.title} (${c.priceLabel})`}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">Auto-fills the caption and photos from your inventory.</p>
            </div>

            <div>
              <label className={labelCls}>Post to</label>
              <div className="flex gap-2">
                <button type="button" data-testid="channel-facebook" onClick={() => toggleChannel("facebook")} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${channels.includes("facebook") ? "gpm-text-cream border-transparent" : "text-gray-500 border-black/20"}`} style={channels.includes("facebook") ? { backgroundColor: "#1877F2" } : {}}>
                  <Facebook size={16} /> Facebook
                </button>
                <button type="button" data-testid="channel-instagram" onClick={() => toggleChannel("instagram")} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${channels.includes("instagram") ? "text-white border-transparent" : "text-gray-500 border-black/20"}`} style={channels.includes("instagram") ? { background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" } : {}}>
                  <Instagram size={16} /> Instagram
                </button>
              </div>
            </div>

            <div>
              <label className={labelCls}>Caption</label>
              <textarea data-testid="composer-caption" rows={9} className={inputCls} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Write your post…" />
            </div>

            <div>
              <label className={labelCls}>Photos</label>
              {images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {images.map((img, i) => (
                    <div key={i} className="relative group aspect-square">
                      <img src={imgSrc(img)} alt="" className="w-full h-full object-cover border border-black/10" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => doUpload(e.target.files)} />
              <button type="button" data-testid="composer-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-3 py-1.5 border border-black/20 text-sm font-semibold disabled:opacity-60">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add photos
              </button>
            </div>

            <div>
              <label className={labelCls}><CalendarClock size={12} className="inline mr-1" /> Schedule for (optional)</label>
              <input data-testid="composer-schedule" type="datetime-local" className={inputCls} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
          </div>

          {/* Right: preview */}
          <div>
            <label className={labelCls}>Preview</label>
            <div className="border border-black/10 bg-white">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-black/5">
                <div className="w-8 h-8 rounded-full gpm-bg-green flex items-center justify-center text-[10px] font-bold gpm-text-gold">GPM</div>
                <div className="text-sm font-semibold gpm-text-green">GPM Classics</div>
                <div className="ml-auto flex gap-1">
                  {channels.includes("facebook") && <Facebook size={14} style={{ color: "#1877F2" }} />}
                  {channels.includes("instagram") && <Instagram size={14} style={{ color: "#DD2A7B" }} />}
                </div>
              </div>
              {images[0] ? <img src={imgSrc(images[0])} alt="" className="w-full aspect-square object-cover" /> : <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-400 text-sm">No photo</div>}
              <div className="p-3 text-sm whitespace-pre-wrap text-gray-800 max-h-64 overflow-y-auto">{caption || "Your caption will appear here…"}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t border-black/10 bg-gray-50">
          <button data-testid="save-draft" onClick={() => save("draft")} disabled={busy} className="flex items-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold disabled:opacity-60"><FileText size={15} /> Save draft</button>
          <button data-testid="schedule-post" onClick={() => save("scheduled")} disabled={busy} className="flex items-center gap-2 px-4 py-2.5 border-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--gpm-gold)", color: "var(--gpm-green)" }}><CalendarClock size={15} /> Schedule</button>
          <button data-testid="publish-post" onClick={() => save("published")} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Publish now</button>
        </div>
      </div>
    </div>
  );
};

export default PostComposer;
